import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './App.css';

const QUESTION_COUNT_OPTIONS = [10, 20, 50, 'Alle'];

const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const App = () => {
  const [allQuestions, setAllQuestions] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [quizStarted, setQuizStarted] = useState(false);
  const [quizFinished, setQuizFinished] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCount, setSelectedCount] = useState(20);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [reporting, setReporting] = useState(false);
  const [reportComment, setReportComment] = useState('');
  const [reportStatus, setReportStatus] = useState(null);

  const handleOpenReport = () => {
    setReporting(true);
    setReportComment('');
    setReportStatus(null);
  };

  const handleCloseReport = () => {
    setReporting(false);
    setReportComment('');
    setReportStatus(null);
  };

  const handleSubmitReport = async (e) => {
    e.preventDefault();
    setReportStatus('submitting');
    try {
      await axios.post('/api/reports', {
        questionId: questions[currentQuestionIndex].id,
        officialQid: questions[currentQuestionIndex].official_qid,
        question: questions[currentQuestionIndex].question,
        comment: reportComment
      });
      setReportStatus('success');
      setTimeout(() => {
        setReporting(false);
        setReportStatus(null);
      }, 1500);
    } catch (err) {
      console.error('Error submitting report:', err);
      setReportStatus('error');
    }
  };

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const response = await axios.get('/api/questions');
        setAllQuestions(response.data);
      } catch (err) {
        setError('Vragen konden niet worden geladen. Probeer opnieuw.');
        console.error('Error fetching questions:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchQuestions();
  }, []);

  const handleStartQuiz = () => {
    const shuffled = shuffleArray(allQuestions);
    const count = selectedCount === 'Alle' ? shuffled.length : selectedCount;
    const subset = shuffled.slice(0, count);
    setQuestions(subset);
    setTotalQuestions(subset.length);
    setCurrentQuestionIndex(0);
    setScore(0);
    setAnsweredCount(0);
    setSelectedAnswer(null);
    setShowExplanation(false);
    setQuizFinished(false);
    setQuizStarted(true);
  };

  const handleAnswerSelection = (answer) => {
    setSelectedAnswer(answer);
    setShowExplanation(true);
    if (answer === questions[currentQuestionIndex].correctAnswer) {
      setScore((prev) => prev + 1);
    }
    setAnsweredCount((prev) => prev + 1);
  };

  const handleNextQuestion = () => {
    const remaining = [...questions];
    remaining.splice(currentQuestionIndex, 1);

    if (remaining.length === 0) {
      setQuizFinished(true);
    } else {
      setQuestions(remaining);
      setCurrentQuestionIndex(0);
      setShowExplanation(false);
      setSelectedAnswer(null);
    }
  };

  const handleRestartQuiz = () => {
    setQuizStarted(false);
    setQuizFinished(false);
    setScore(0);
    setAnsweredCount(0);
    setSelectedAnswer(null);
    setShowExplanation(false);
  };

  if (loading) {
    return (
      <div className="quiz-container">
        <div className="quiz-card loading-card">
          <div className="spinner" aria-label="Laden"></div>
          <p>Vragen ophalen...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="quiz-container">
        <div className="quiz-card error-card">
          <p className="error-message">{error}</p>
          <button className="btn btn-primary" onClick={() => window.location.reload()}>
            Opnieuw proberen
          </button>
        </div>
      </div>
    );
  }

  if (quizFinished) {
    const percentage = Math.round((score / totalQuestions) * 100);
    const passed = percentage >= 60;
    return (
      <div className="quiz-container">
        <div className="quiz-card results-card">
          <h1>HVAC Quiz</h1>
          <div className={`result-badge ${passed ? 'passed' : 'failed'}`}>
            {passed ? '✓ Geslaagd' : '✗ Niet geslaagd'}
          </div>
          <div className="score-display">
            <span className="score-number">{score}</span>
            <span className="score-divider">/</span>
            <span className="score-total">{totalQuestions}</span>
          </div>
          <p className="score-percentage">{percentage}%</p>
          <p className="score-message">
            {percentage === 100
              ? 'Perfect! Uitstekend gedaan!'
              : passed
              ? 'Goed gedaan! Je hebt de quiz gehaald.'
              : 'Blijf oefenen! Je scoort min. 60% om te slagen.'}
          </p>
          <button className="btn btn-primary" onClick={handleRestartQuiz}>
            Opnieuw starten
          </button>
        </div>
      </div>
    );
  }

  if (!quizStarted) {
    return (
      <div className="quiz-container">
        <div className="quiz-card start-card">
          <h1>HVAC Quiz</h1>
          <p className="subtitle">Voorbereiding examen koeltechnieker</p>
          <p className="question-bank-info">
            {allQuestions.length} vragen beschikbaar
          </p>
          <div className="count-selector">
            <label htmlFor="question-count">Aantal vragen:</label>
            <div className="count-options" id="question-count">
              {QUESTION_COUNT_OPTIONS.map((option) => (
                <button
                  key={option}
                  className={`count-btn ${selectedCount === option ? 'active' : ''}`}
                  onClick={() => setSelectedCount(option)}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
          <button className="btn btn-primary btn-large" onClick={handleStartQuiz}>
            Start Quiz
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const progressPercent = Math.round((answeredCount / totalQuestions) * 100);

  return (
    <div className="quiz-container">
      <div className="quiz-card question-card">
        <div className="quiz-header">
          <span className="progress-label">
            Vraag {answeredCount + 1} / {totalQuestions}
          </span>
          <span className="question-ref">
            #{currentQuestion.id}
            <button
              type="button"
              className="btn-report-link"
              onClick={handleOpenReport}
              title="Fout gevonden in deze vraag? Meld het hier"
            >
              ⚠️ Fout melden
            </button>
          </span>
          <span className="score-label">Score: {score}</span>
        </div>
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progressPercent}%` }}></div>
        </div>

        {!showExplanation ? (
          <>
            <h2 className="question-text">{currentQuestion.question}</h2>
            <div className="options-list">
              {currentQuestion.options.map((option, index) => (
                <button
                  key={index}
                  className="option-btn"
                  onClick={() => handleAnswerSelection(option)}
                  disabled={selectedAnswer !== null}
                >
                  <span className="option-letter">
                    {String.fromCharCode(65 + index)}
                  </span>
                  {option}
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <h2 className="question-text">{currentQuestion.question}</h2>
            <div className="feedback-section">
              <div
                className={`answer-feedback ${
                  selectedAnswer === currentQuestion.correctAnswer
                    ? 'correct'
                    : 'incorrect'
                }`}
              >
                {selectedAnswer === currentQuestion.correctAnswer
                  ? '✓ Correct!'
                  : '✗ Fout'}
              </div>
              <div className="answer-details">
                <p>
                  <strong>Jouw antwoord:</strong>{' '}
                  <span
                    className={
                      selectedAnswer === currentQuestion.correctAnswer
                        ? 'correct-text'
                        : 'incorrect-text'
                    }
                  >
                    {selectedAnswer}
                  </span>
                </p>
                {selectedAnswer !== currentQuestion.correctAnswer && (
                  <p>
                    <strong>Correct antwoord:</strong>{' '}
                    <span className="correct-text">
                      {currentQuestion.correctAnswer}
                    </span>
                  </p>
                )}
              </div>
              {currentQuestion.explanation && (
                <div className="explanation-box">
                  <strong>Uitleg:</strong>
                  <p>{currentQuestion.explanation}</p>
                </div>
              )}
            </div>
            <button className="btn btn-primary" onClick={handleNextQuestion}>
              {questions.length === 1 ? 'Resultaten bekijken' : 'Volgende vraag →'}
            </button>
          </>
        )}

        {reporting && (
          <div className="report-modal-overlay" onClick={handleCloseReport}>
            <div className="report-modal" onClick={(e) => e.stopPropagation()}>
              <h3>⚠️ Fout melden bij vraag #{currentQuestion.id}</h3>
              <p className="report-question-snippet">"{currentQuestion.question}"</p>
              {reportStatus === 'success' ? (
                <div className="report-success-msg">✓ Bedankt voor je melding! We kijken het na.</div>
              ) : (
                <form onSubmit={handleSubmitReport}>
                  <label htmlFor="report-textarea">Wat klopt er niet? (optioneel):</label>
                  <textarea
                    id="report-textarea"
                    rows="3"
                    placeholder="Bijv. antwoord B klopt niet volgens mij, typfout in optie..."
                    value={reportComment}
                    onChange={(e) => setReportComment(e.target.value)}
                    autoFocus
                  />
                  {reportStatus === 'error' && (
                    <p className="report-error-msg">Versturen mislukt. Probeer opnieuw.</p>
                  )}
                  <div className="report-actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleCloseReport}
                      disabled={reportStatus === 'submitting'}
                    >
                      Annuleren
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={reportStatus === 'submitting'}
                    >
                      {reportStatus === 'submitting' ? 'Versturen...' : 'Versturen'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default App;